/**
 * Profile – Children manager
 * Rendering and handlers for the children section in Family
 */

import { CHILD_GENDER_OPTIONS } from '../../options';
import { wantsKidsLabel } from '../../utils';
import { store } from '../../state';
import { showToast } from '../../main';
import { TOAST_FAILED_SAVE_CHILDREN } from '../../utils/app-strings';
import type { ChildInfo } from '@couple/shared-options';

export function calculateAgeFromBirthday(birthday: string): number {
  const birthDate = new Date(birthday);
  const today = new Date();
  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDiff = today.getMonth() - birthDate.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
    age--;
  }
  return age;
}

export function formatChildDisplay(child: { birthday?: string; gender?: string; lives_with_me?: number }): string {
  const parts: string[] = [];
  if (child.birthday) {
    const age = calculateAgeFromBirthday(child.birthday);
    parts.push(age.toString());
  }
  if (child.gender) {
    const genderLabel = CHILD_GENDER_OPTIONS.find(o => o.value === child.gender)?.label || child.gender;
    parts.push(genderLabel);
  }
  if (child.lives_with_me !== undefined) {
    parts.push(`(${child.lives_with_me}%)`);
  }
  return parts.join(' ');
}

export function renderChildrenList(children: Array<{ birthday?: string; gender?: string; lives_with_me?: number }>): string {
  if (!children || children.length === 0) {
    return '<div class="empty-children-message">No children added yet</div>';
  }

  return children.map((child, index) => `
    <div class="child-entry" data-child-index="${index}">
      <div class="child-entry-header">
        <span class="child-number">Child ${index + 1}</span>
        <span class="child-summary">${formatChildDisplay(child)}</span>
        <button type="button" class="btn-icon btn-remove-child" data-child-index="${index}" title="Remove child">
          <i class="fas fa-times"></i>
        </button>
      </div>
      <div class="child-entry-fields">
        <div class="child-field">
          <span class="group-label">Birthday</span>
          <input
            type="date"
            class="input child-birthday"
            value="${child.birthday || ''}"
            data-child-index="${index}"
            max="${new Date().toISOString().split('T')[0]}"
          />
        </div>
        <div class="child-field">
          <span class="group-label">Gender</span>
          <select class="input child-gender" data-child-index="${index}">
            <option value="">Select</option>
            ${CHILD_GENDER_OPTIONS.map(opt =>
              `<option value="${opt.value}" ${child.gender === opt.value ? 'selected' : ''}>${opt.label}</option>`
            ).join('')}
          </select>
        </div>
        <div class="child-field child-field-slider">
          <span class="group-label">Lives with me: <span class="lives-with-value">${child.lives_with_me ?? 50}%</span></span>
          <input
            type="range"
            class="input child-lives-with"
            min="0"
            max="100"
            step="10"
            value="${child.lives_with_me ?? 50}"
            data-child-index="${index}"
          />
          <div class="slider-ticks">
            <span>0%</span>
            <span>20%</span>
            <span>40%</span>
            <span>60%</span>
            <span>80%</span>
            <span>100%</span>
          </div>
        </div>
      </div>
    </div>
  `).join('');
}

export function setupChildrenHandlers(): void {
  const hasKidsGroup = document.getElementById('has_kids');
  const childrenSection = document.getElementById('children-section');
  const childrenList = document.getElementById('children-list');
  const addChildBtn = document.getElementById('add-child-btn');

  if (!hasKidsGroup || !childrenSection || !childrenList || !addChildBtn) return;

  // Get current children from state
  const state = store.getState();
  let children: Array<{ birthday?: string; gender?: string; lives_with_me?: number }> =
    state.user?.profile?.profile_json?.kids?.children || [];

  // Show/hide children section based on has_kids value
  const updateChildrenSectionVisibility = () => {
    const checkedRadio = hasKidsGroup.querySelector('input[type="radio"]:checked') as HTMLInputElement;
    const hasKids = checkedRadio?.value === 'yes';
    childrenSection.style.display = hasKids ? '' : 'none';

    // Update "wants more kids" label to match has_kids state
    const wantsMoreIndicator = document.getElementById('indicator-wants_more_kids');
    const wantsMoreLabel = wantsMoreIndicator?.parentElement?.querySelector('.group-label');
    if (wantsMoreLabel) {
      wantsMoreLabel.textContent = wantsKidsLabel(hasKids, 'profile');
    }

    // If changed to 'no', clear children array and save
    if (!hasKids && children.length > 0) {
      children = [];
      saveChildren();
      childrenList.innerHTML = renderChildrenList([]);
    }
  };

  hasKidsGroup.querySelectorAll('input[type="radio"]').forEach(radio => {
    radio.addEventListener('change', updateChildrenSectionVisibility);
  });

  // Also update when PNTA checkbox changes (it unchecks radios without dispatching change)
  const hasKidsPnta = document.querySelector('input[data-pnta-for-group="has_kids"]');
  hasKidsPnta?.addEventListener('change', updateChildrenSectionVisibility);

  // Add child button handler
  addChildBtn.addEventListener('click', () => {
    children.push({ birthday: '', gender: '', lives_with_me: 50 });
    childrenList.innerHTML = renderChildrenList(children);
    setupChildFieldListeners();
    saveChildren();
  });

  // Save children to profile_json
  const saveChildren = async () => {
    try {
      const { profileApi } = await import('../../api');
      const currentState = store.getState();
      const currentProfileJson = currentState.user?.profile?.profile_json || {};
      const currentKids = currentProfileJson.kids || {};

      // Clean up children data: filter out empty entries and strip empty strings
      const cleanedChildren = children
        .filter(c => c.birthday || c.gender)
        .map(c => ({
          ...(c.birthday ? { birthday: c.birthday } : {}),
          ...(c.gender ? { gender: c.gender } : {}),
          lives_with_me: c.lives_with_me,
        })) as ChildInfo[];

      const updatedProfileJson = {
        ...currentProfileJson,
        kids: {
          ...currentKids,
          children: cleanedChildren
        }
      };

      await profileApi.update({ profile_json: updatedProfileJson });

      // Update local state
      store.setState({
        user: {
          ...currentState.user!,
          profile: {
            ...currentState.user!.profile!,
            profile_json: updatedProfileJson
          }
        }
      });

      // Show save indicator
      const indicator = document.getElementById('indicator-children');
      if (indicator) {
        indicator.classList.add('visible');
        setTimeout(() => indicator.classList.remove('visible'), 2000);
      }
    } catch (err) {
      console.error('Failed to save children:', err);
      showToast(TOAST_FAILED_SAVE_CHILDREN, 'error');
    }
  };

  // Setup listeners for child fields
  const setupChildFieldListeners = () => {
    // Remove child buttons
    childrenList.querySelectorAll('.btn-remove-child').forEach(btn => {
      btn.addEventListener('click', (e) => {
        const index = parseInt((e.currentTarget as HTMLElement).dataset.childIndex || '0');
        children.splice(index, 1);
        childrenList.innerHTML = renderChildrenList(children);
        setupChildFieldListeners();
        saveChildren();
      });
    });

    // Birthday inputs
    childrenList.querySelectorAll('.child-birthday').forEach(input => {
      input.addEventListener('change', (e) => {
        const index = parseInt((e.target as HTMLElement).dataset.childIndex || '0');
        children[index].birthday = (e.target as HTMLInputElement).value;
        // Update summary display
        const entry = (e.target as HTMLElement).closest('.child-entry');
        const summary = entry?.querySelector('.child-summary');
        if (summary) summary.textContent = formatChildDisplay(children[index]);
        saveChildren();
      });
    });

    // Gender selects
    childrenList.querySelectorAll('.child-gender').forEach(select => {
      select.addEventListener('change', (e) => {
        const index = parseInt((e.target as HTMLElement).dataset.childIndex || '0');
        children[index].gender = (e.target as HTMLSelectElement).value;
        // Update summary display
        const entry = (e.target as HTMLElement).closest('.child-entry');
        const summary = entry?.querySelector('.child-summary');
        if (summary) summary.textContent = formatChildDisplay(children[index]);
        saveChildren();
      });
    });

    // Lives with me sliders
    childrenList.querySelectorAll('.child-lives-with').forEach(slider => {
      slider.addEventListener('input', (e) => {
        const index = parseInt((e.target as HTMLElement).dataset.childIndex || '0');
        const value = parseInt((e.target as HTMLInputElement).value);
        children[index].lives_with_me = value;
        // Update display value
        const entry = (e.target as HTMLElement).closest('.child-entry');
        const valueDisplay = entry?.querySelector('.lives-with-value');
        if (valueDisplay) valueDisplay.textContent = `${value}%`;
        // Update summary display
        const summary = entry?.querySelector('.child-summary');
        if (summary) summary.textContent = formatChildDisplay(children[index]);
      });
      slider.addEventListener('change', () => {
        saveChildren();
      });
    });
  };

  // Initial setup of field listeners
  setupChildFieldListeners();
}
